// src/views/add/Add2.js

import React, { useEffect, useState, useRef } from "react";
import Popup from "reactjs-popup";
import SignatureCanvas from "react-signature-canvas";
import { useNavigate } from "react-router-dom";
import "reactjs-popup/dist/index.css";
import { FaTimesCircle } from "react-icons/fa";
import { registerLocale } from "react-datepicker";
import { et } from "date-fns/locale";
import {
  fetchMeterBrands,
  fetchQnoms,
  fetchDiameters,
} from "../../data/fetchData";
import { useNotification } from "../../context/NotificationContext";
import { formatDecimal } from "../../utils/formatters";

registerLocale("et", et);

const TYPE_LABELS = {
  MAIN_GAUGE: "Põhiarvesti",
  SUB_GAUGE: "Alamarvesti",
  WATERING: "Kastmine",
};

export default function Add2() {
  const [data, setData] = useState({});
  const [brandOptions, setBrandOptions] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);
  const [diamOptions, setDiamOptions] = useState([]);
  const sigCanvas = useRef({});
  const navigate = useNavigate();
  const { showError } = useNotification();

  useEffect(() => {
    // Load draft data from Add1
    const draft = localStorage.getItem("addData");
    if (draft) {
      setData(JSON.parse(draft));
    }

    // Load lookup data
    fetchMeterBrands().then(setBrandOptions).catch(console.error);
    fetchQnoms().then(setQnOptions).catch(console.error);
    fetchDiameters().then(setDiamOptions).catch(console.error);
  }, []);

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("et-EE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  const actionLabel = data.workType || "—";
  const typeLabel = TYPE_LABELS[data.meterType] || data.meterType || "—";

  const brandName = (() => {
    const b = brandOptions.find((b) => b.id === Number(data.veemootjaMark));
    return b ? b.name : data.veemootjaMark || "—";
  })();

  const qNomLabel = (() => {
    const q = qnOptions.find((q) => q.id === Number(data.qn));
    return q ? formatDecimal(q.q_nom) : data.qn || "—";
  })();

  const diamLabel = (() => {
    const d = diamOptions.find((d) => d.id === Number(data.diameter));
    return d ? formatDecimal(d.diameter) : data.diameter || "—";
  })();

  const handleBackEdit = () => {
    // Save current data to localStorage before going back
    const currentData = { ...data };
    localStorage.setItem("addData", JSON.stringify(currentData));

    // Navigate back to Add1 preserving component state
    navigate(-1);
  };

  const clearSig = () => {
    sigCanvas.current.clear();
  };

  // Validate that all required fields are filled before allowing signature save
  const validateBeforeSign = () => {
    // List of keys that must be truthy (notes is explicitly excluded)
    const requiredKeys = [
      "estate",
      "client",
      "tehaseNumber",
      "arvestiNait",
      "veemootjaMark",
      "qn",
      "diameter",
      "meterType",
      "sealNumber",
      "workDate",
      "viimaneTaatlemine",
      "sealDate",
    ];

    for (const key of requiredKeys) {
      // For numeric zero we still treat 0 as valid, check null/undefined/empty string
      if (
        data[key] === undefined ||
        data[key] === null ||
        (typeof data[key] === "string" && data[key].trim() === "")
      ) {
        showError("Kõik lahtrid (v.a märkus) peavad olema täidetud enne allkirjastamist.");
        return false;
      }
    }
    return true;
  };

  const saveAndNext = () => {
    if (!validateBeforeSign()) return;
    const signature = sigCanvas.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    const combinedData = { ...data, signature, id: data.id || null };
    localStorage.setItem("addData", JSON.stringify(combinedData));
    navigate("/Add3");
  };

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <div className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Kokkuvõte</h1>
      </div>

      <table className="w-full table-auto">
        <tbody>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Veemõõtja number</th>
            <td className="p-2 border-t border-gray-200">{data.tehaseNumber || "—"}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Kuupäev</th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(data.workDate)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Aadress</th>
            <td className="p-2 border-t border-gray-200">
              {data.aadress || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Lepingupartner</th>
            <td className="p-2 border-t border-gray-200">
              {data.client_name || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Töö liik</th>
            <td className="p-2 border-t border-gray-200">{actionLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Tüüp</th>
            <td className="p-2 border-t border-gray-200">{typeLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Arvesti mark
            </th>
            <td className="p-2 border-t border-gray-200">{brandName}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Qn/m³</th>
            <td className="p-2 border-t border-gray-200">{qNomLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Läbimõõt</th>
            <td className="p-2 border-t border-gray-200">{diamLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Arvesti näit
            </th>
            <td className="p-2 border-t border-gray-200">
              {formatDecimal(data.arvestiNait) || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Viimane taatlemine
            </th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(data.viimaneTaatlemine)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Plommimise kuupäev
            </th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(data.sealDate)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Plommi number
            </th>
            <td className="p-2 border-t border-gray-200">
              {data.sealNumber || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Konsoolil
            </th>
            <td className="p-2 border-t border-gray-200">
              {data.meterIsOnConsole ?? "—"}
            </td>
          </tr>
          {data.notes && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Märkused
              </th>
              <td className="p-2 border-t border-gray-200">{data.notes}</td>
            </tr>
          )}
          {/* signature */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Allkiri</th>
            <td className="p-2 border-t border-gray-200">
              {data.signature ? (
                <Popup
                  modal
                  trigger={
                    <img
                      src={data.signature}
                      alt="Signature"
                      className="w-24 h-auto object-cover rounded-md cursor-pointer"
                    />
                  }
                  closeOnDocumentClick={false}
                >
                  {(close) => (
                    <div className="relative p-4">
                      <img
                        src={data.signature}
                        alt="Signature"
                        className="w-full h-auto max-w-3xl mx-auto"
                      />
                      <button
                        onClick={close}
                        className="absolute top-2 right-2 text-2xl"
                      >
                        <FaTimesCircle />
                      </button>
                    </div>
                  )}
                </Popup>
              ) : (
                <em>Pole allkirja</em>
              )}
            </td>
          </tr>
          {/* images */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Pildid</th>
            <td className="p-2 border-t border-gray-200">
              <div className="flex space-x-4">
                {data.images?.map((img, idx) => (
                  <Popup
                    key={idx}
                    modal
                    trigger={
                      <img
                        src={img}
                        alt={`Work ${idx + 1}`}
                        className="w-24 h-24 object-cover rounded-md cursor-pointer"
                      />
                    }
                    closeOnDocumentClick={false}
                  >
                    {(close) => (
                      <div className="relative p-4">
                        <img
                          src={img}
                          alt={`Work ${idx + 1}`}
                          className="w-full h-auto max-w-3xl mx-auto"
                        />
                        <button
                          onClick={close}
                          className="absolute top-2 right-2 text-2xl"
                        >
                          <FaTimesCircle />
                        </button>
                      </div>
                    )}
                  </Popup>
                ))}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleBackEdit}
          className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
        >
          Tagasi
        </button>
        <div className="flex gap-4">
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            onClick={() => {
              const combined = {
                ...data,
                signature: null,
                id: data.id || null,
              };
              localStorage.setItem("addData", JSON.stringify(combined));
              navigate("/Add3");
            }}
          >
            Saada allkirjata
          </button>
          <Popup
            modal
            trigger={
              <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover">
                Allkirjasta
              </button>
            }
            closeOnDocumentClick={true}
            overlayStyle={{ backgroundColor: "rgba(0,0,0,0.6)" }}
            contentStyle={{ maxWidth: "90%", width: "1200px", margin: "auto" }}
          >
            {(close) => (
              <div className="p-4">
                <div className="bg-white p-6 rounded-md shadow-lg relative">
                  <SignatureCanvas
                    ref={sigCanvas}
                    minWidth={1}
                    maxWidth={5}
                    canvasProps={{
                      className:
                        "signatureCanvas border border-gray-300 rounded-md",
                      style: { width: "100%", height: "60vh" },
                    }}
                  />
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={close}
                      className="text-2xl font-bold text-[var(--color-secondary)] hover:text-[var(--color-secondary-hover)] bg-white rounded-full p-1"
                    >
                      <FaTimesCircle size={40} />
                    </button>
                  </div>
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={clearSig}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Tühjenda
                    </button>
                    <button
                      onClick={saveAndNext}
                      className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                    >
                      Salvesta
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Popup>
        </div>
      </div>
    </div>
  );
}
